import React, { FC, useCallback, useMemo } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Button from '@atlaskit/button';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import EditIcon from '@atlaskit/icon/glyph/edit';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import isNil from 'lodash/isNil';

import { ExternalLinkAction, KeyValue, useActions } from '@/components/primitive';
import { DAC_FAQS_DOC_LINK } from '@/constants';
import {
  cursorPointer,
  flexStart,
  spaceGapCompact,
  spaceGapCozy,
  spaceGapMinimum,
  spaceGapRelaxed,
  spaceTopCozy,
  spaceTopPaddingCompact,
  tooltipStyls,
} from '@/styles';
import { EditContext, useLocationController } from '@/controllers';
import { localNumberFormat } from '@/utils';

import { faqStyles } from '../summary-helpers/styles';

import { LowTrafficProps } from './types';
import * as styles from './styles';
import { getLowTrafficThreshold } from './utils';
import { LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE } from './constants';
import LowTrafficCalculator from './low-traffic-calculator';
import ItemPeriodValue from './item-period-value';

const LowTrafficPopup: FC<LowTrafficProps> = ({
  lowTrafficStatus,
  objective,
  publicId,
  closePopup,
}) => {
  const { isSLOGroupMemberPage } = useLocationController();
  const { dispatch: dispatchEdit } = React.useContext(EditContext);

  const thresholdRecommended = useMemo(
    () => getLowTrafficThreshold(objective, LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE),
    [objective]
  );

  const { onCopy } = useActions(thresholdRecommended.toString(), undefined, 'slo');
  const { onEdit } = useActions(publicId, undefined, 'slo');

  const handleEdit = useCallback(() => {
    dispatchEdit({ highlights: { lowTrafficThreshold: true } });
    onEdit();
    if (closePopup) closePopup();
  }, [dispatchEdit, onEdit, closePopup]);

  if (!lowTrafficStatus) return <></>;

  const { currentTraffic, lowTrafficThreshold, inLowTrafficState } = lowTrafficStatus;

  // Note: it could also be 0 as a configured value
  const thresholdConfigured = !isNil(lowTrafficThreshold);

  // Threshold has been configured but less than the recommended value, show warning.
  const thresholdConfiguredTooLow =
    thresholdConfigured && lowTrafficThreshold < thresholdRecommended;

  return (
    <div css={styles.sloLowTrafficHelperStyles}>
      <h5>SLO Low Traffic Details</h5>
      <section css={[spaceTopCozy, flexStart, spaceGapCozy, styles.infoBarStyles]}>
        {inLowTrafficState && (
          <div css={[flexStart, spaceGapCompact]}>
            <span>This SLO is in</span>
            <Lozenge appearance={thresholdConfigured ? 'new' : 'removed'} isBold>
              low traffic
            </Lozenge>
            <span>state</span>
          </div>
        )}
        <div css={[flexStart, spaceGapRelaxed]}>
          <KeyValue title="total events" noPositionFix>
            <ItemPeriodValue period="28 days" count={currentTraffic} />
          </KeyValue>
          <KeyValue title="low traffic threshold" noPositionFix>
            <span css={[flexStart, spaceGapMinimum]}>
              <Lozenge
                appearance={!thresholdConfigured || thresholdConfiguredTooLow ? 'removed' : 'new'}
              >
                {thresholdConfigured ? localNumberFormat(lowTrafficThreshold) : 'not set'}
              </Lozenge>
              {!thresholdConfigured && (
                <Tooltip
                  content={
                    <span css={tooltipStyls}>
                      No specific low traffic threshold has been set for this SLO, in this case we
                      use a{' '}
                      <ExternalLinkAction
                        text="default"
                        href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#what-is-the-default-low-traffic-threshold-`}
                        hideIcon
                      />{' '}
                      threshold to mark low traffic state.
                    </span>
                  }
                  position="right"
                >
                  <QuestionCircleIcon
                    label="question-icon"
                    size="small"
                    primaryColor={token('color.text.subtlest')}
                  />
                </Tooltip>
              )}
              <img alt="/" src="/static/slash.svg" />
              <span css={styles.postfixStyles}>28 days</span>
            </span>
          </KeyValue>
        </div>
      </section>
      <section css={[spaceTopCozy, spaceTopPaddingCompact]}>
        <div css={[flexStart, spaceGapCompact]}>
          <SettingsIcon size="small" label="" primaryColor={token('color.icon.accent.blue')} />
          <span>Our recommended</span>
          <Lozenge appearance="default">low traffic threshold</Lozenge>
          <span>:</span>
          <ItemPeriodValue period="28 days" count={thresholdRecommended} />
          <span css={cursorPointer} onClick={onCopy}>
            <Tooltip position="top" content="Copy recommended value">
              <CopyIcon size="small" label="" />
            </Tooltip>
          </span>
        </div>
      </section>
      <section css={[spaceTopCozy]}>
        <LowTrafficCalculator objective={objective} publicId={publicId} />
      </section>
      <section css={[spaceTopCozy, flexStart, spaceGapCompact]}>
        <Button label="cancel" onClick={closePopup}>
          Cancel
        </Button>
        <Button
          label="edit"
          appearance="primary"
          iconBefore={
            isSLOGroupMemberPage ? (
              <SettingsIcon size="small" label="Edit" />
            ) : (
              <EditIcon size="small" label="Edit" />
            )
          }
          onClick={handleEdit}
        >
          {isSLOGroupMemberPage ? 'View Config' : 'Edit'}
        </Button>
      </section>
      <section css={[spaceTopCozy, faqStyles]}>
        <p>
          <b>FAQs</b>
        </p>
        <p>
          What is the benefit of setting a low traffic threshold for my SLO if it has low traffic?
          <ExternalLinkAction
            text=""
            href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#what-is-the-benefit-of-configuring-a-low-traffic-threshold-for-my-slo-if-it-experiences-low-traffic-`}
          />
        </p>
      </section>
    </div>
  );
};

export default LowTrafficPopup;
